import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { auth, googleProvider, signInWithPopup, signInWithEmailAndPassword } from '../../services/firebase';
import { apiFetch } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const Login: React.FC = () => {
    const navigate = useNavigate();
    const { updateRole } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const location = useLocation();
    const from = (location.state as any)?.from?.pathname || '/dashboard';

    const handleEmailLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            const data = await apiFetch('/auth/sync-profile', { method: 'POST' });
            if (data.profile?.role) {
                updateRole(data.profile.role);
            }
            if (data.profile?.role === 'admin') {
                navigate('/admin');
            } else {
                navigate(from === '/' ? '/dashboard' : from);
            }
        } catch (err: any) {
            console.error('Email login error:', err);
            setError('Invalid email or password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        setError(null);
        try {
            await signInWithPopup(auth, googleProvider);
            const data = await apiFetch('/auth/sync-profile', { method: 'POST' });
            if (data.profile?.role) {
                updateRole(data.profile.role);
            }
            if (data.profile?.role === 'admin') {
                navigate('/admin');
            } else {
                navigate(from === '/' ? '/dashboard' : from);
            }
        } catch (err: any) {
            console.error('Google login error:', err);
            setError(err.message || 'Failed to login with Google');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 px-4 animate-fade-in-up relative" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1a2d50 100%)' }}>
            
            {/* Background Accent */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-logo-gold-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
                <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
            </div>

            <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
                <Link to="/" className="inline-block flex justify-center mb-6 hover:scale-105 transition-transform">
                    <img 
                        src="https://static.readdy.ai/image/3d693276a5424479ffa5f1cc868e7c58/99585d34dbb7c267061331244ebc5e82.png" 
                        alt="Rigland Travels" 
                        className="h-20 sm:h-24 w-auto drop-shadow-xl"
                    />
                </Link>
                <h2 className="mt-2 text-center text-3xl font-extrabold text-white tracking-tight">
                    Welcome back
                </h2>
                <p className="mt-2 text-center text-sm text-slate-300">
                    New to Rigland?{' '}
                    <Link to="/auth/signup" className="font-semibold text-logo-gold-400 hover:text-logo-gold-300 transition-colors">
                        Create an account
                    </Link>
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10 w-full">
                <div className="bg-navy-900/60 backdrop-blur-xl py-8 px-6 sm:rounded-3xl shadow-2xl border border-white/10 w-full">
                    
                    {error && (
                        <div className="mb-6 bg-red-900/50 backdrop-blur-sm border-l-4 border-red-500 p-4 rounded-r-lg">
                            <p className="text-sm text-red-200 font-medium flex items-center">
                                <i className="ri-error-warning-line mr-2 text-lg"></i>
                                {error}
                            </p>
                        </div>
                    )}

                    <form className="space-y-6" onSubmit={handleEmailLogin}>
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1">Email Address</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <i className="ri-mail-line text-slate-400"></i>
                                </div>
                                <input 
                                    type="email" 
                                    required 
                                    value={email} 
                                    onChange={(e) => setEmail(e.target.value)} 
                                    className="block w-full pl-10 pr-3 py-3 border border-slate-700 rounded-xl bg-navy-800/50 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-logo-gold-500 focus:border-logo-gold-500 sm:text-sm transition-all" 
                                    placeholder="Enter your email"
                                />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-sm font-medium text-slate-300">Password</label>
                                <a href="#" className="text-xs font-medium text-logo-gold-400 hover:text-logo-gold-300 transition-colors">Forgot password?</a>
                            </div>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <i className="ri-lock-line text-slate-400"></i>
                                </div>
                                <input 
                                    type={showPassword ? "text" : "password"} 
                                    required 
                                    value={password} 
                                    onChange={(e) => setPassword(e.target.value)} 
                                    className="block w-full pl-10 pr-10 py-3 border border-slate-700 rounded-xl bg-navy-800/50 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-logo-gold-500 focus:border-logo-gold-500 sm:text-sm transition-all" 
                                    placeholder="••••••••"
                                />
                                <button 
                                    type="button" 
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-white transition-colors"
                                >
                                    <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                                </button>
                            </div>
                        </div>

                        <div>
                            <button 
                                type="submit" 
                                disabled={loading} 
                                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-lg text-sm font-bold text-navy-900 bg-gradient-to-r from-logo-gold-400 to-logo-gold-600 hover:from-logo-gold-300 hover:to-logo-gold-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-navy-900 focus:ring-logo-gold-500 disabled:opacity-50 transition-all transform hover:scale-[1.02]"
                            >
                                {loading ? (
                                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                                ) : (
                                    'Sign In'
                                )}
                            </button>
                        </div>
                    </form>

                    <div className="mt-8">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-slate-700"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-4 bg-navy-900 text-slate-400">Or continue with</span>
                            </div>
                        </div>

                        <div className="mt-6">
                            <button
                                onClick={handleGoogleLogin}
                                disabled={loading}
                                className="w-full flex justify-center items-center py-3 px-4 border border-slate-600 rounded-xl shadow-sm text-sm font-bold text-white bg-navy-800 hover:bg-navy-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-navy-900 focus:ring-slate-500 disabled:opacity-50 transition-all transform hover:scale-[1.02]"
                            >
                                <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                </svg>
                                Google
                            </button>
                        </div>
                    </div>
                </div>
                <div className="mt-6 text-center">
                    <p className="text-xs text-slate-500 flex items-center justify-center">
                        <i className="ri-lock-2-line mr-1"></i> Secure 256-bit encryption
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
